/* eslint-disable react/jsx-no-bind */
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography } from '@material-ui/core';
import CountyContext from '../../contexts/CountyContext';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import {
  composeWhereClause, displayChildAge, displayFacilityType, displayFamilyType,
} from '../../modules/data';
import { rdsSelect, rdsTabulate } from '../../modules/rds';
import { stateFIPS } from '../../modules/states';
import { toFormattedNumber } from '../../modules/text';
import ResponsiveContainer from '../ResponsiveContainer';
import StateCountyMap from '../StateCountyMap';

function CountyMap() {
  const { stateCode } = useParams();
  const {
    primaryColor, lightColor, darkColor, selectionColor, hoverColor,
  } = useContext(StoryContext);
  const {
    county, setCounty, setCountyCode, countyCode,
  } = useContext(CountyContext);
  const [, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [dataRange, setDataRange] = useState([]);

  const { familyType, settingType, childAge } = useContext(FiltersContext);

  useEffect(() => {
    function getChildrenCount() {
      const dataProduct = 'acs_sams_county_wide';
      const where = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND (county_suppressed=0 OR county_suppressed=NULL)`;

      return rdsSelect('3rdparty', dataProduct, {
        cols: 'county_fips,cnt_parents_inlf_total',
        where,
        format: 'amcharts',
        inject: false,
      });
    }

    function getStateChildrenCount() {
      const dataProduct = 'acs_sams_state_wide';
      const where = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;
      return rdsSelect('3rdparty', dataProduct, {
        cols: 'cnt_parents_inlf_total',
        where,
        format: 'amcharts',
        inject: false,
      });
    }

    function getCapacity() {
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]}  AND capacity_type=LIC`;

      whereClause += composeWhereClause({ settingType, childAge });

      return rdsTabulate('ccdc', 'county_licensed_capacity', {
        dims: 'county_fips',
        measure: 'capacity:SUM(capacity)',
        where: whereClause,
        format: 'amcharts',
        inject: false,
      }, true);
    }

    Promise.all([getCapacity(), getChildrenCount(), getStateChildrenCount()])
      .then((results) => {
        const d = results[0].parsedBody.dataProvider;
        const countyData = results[1].parsedBody.dataProvider;

        const totalCapacity = d.reduce((a, v) => (a + Number.parseInt(v.capacity, 10)), 0);
        const payload = d.map((v) => {
          const result = Object.assign(v, {
            totalCapacity,
            totalChildren: results[2].parsedBody.dataProvider[0].cnt_parents_inlf_total,
          });

          const countyChildren = _.find(countyData, { county_fips: v.county_fips });
          if (countyChildren) {
            result.children = countyChildren.cnt_parents_inlf_total;
          }
          if (result.children) {
            result.value = ((result.capacity / result.children) * 100);
          }
          return result;
        });
        const dr = payload.map((v) => (v.value));
        setData(payload);
        setDataRange(dr);
      })
      .catch((rdsError) => {
        setError(rdsError);
      })
      .finally(() => {
        setReady(true);
      });
  }, [stateCode, settingType, childAge]);

  const toolTipFormatterFunction = function toolTipFormatterFunction(d, dataPoint) {
    if (!dataPoint || !dataPoint.value) {
      return 'No data';
    }
    // // console.log(d);
    // console.log(dataPoint);
    // console.log((dataPoint.totalChildren / dataPoint.totalCapacity) * 100)
    return `<b>${d.properties.name} ${d.properties.TYPE}, ${d.properties.STATE}</b>, has <b>${toFormattedNumber(dataPoint.value)}</b> 
    <b>${displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')}</b> 
    slots for every 100 <b>${displayChildAge(childAge, true).toLowerCase()}</b> whose parents work. 
    <br />
    (Overall in <b>${d.properties.STATE}</b>, there are <b>${toFormattedNumber((dataPoint.totalCapacity / dataPoint.totalChildren) * 100)}</b> 
    <b>${displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')}</b> slots for every 100
    <b>${displayChildAge(childAge, true).toLowerCase()}</b>)`;
  };

  const legendValueFormatter = function legendValueFormatter(v) {
    return Number.parseInt(v, 10);
  };

  const legendLabelFormatter = function legendLabelFormatter(v) {
    return toFormattedNumber(v);
  };

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <ResponsiveContainer aspect={1.5} width="100%">
        <StateCountyMap
          data={data}
          legendValueFormatter={legendValueFormatter}
          legendLabelFormatter={legendLabelFormatter}
          legendTitleText={`The darker the color, the greater the number of available ${displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')} slots for every 100 ${displayChildAge(childAge, true).toLowerCase()}.`}
          dataRange={dataRange}
          stateCode={stateCode}
          colorHigh={darkColor}
          colorLow={lightColor}
          primaryColor={primaryColor}
          selectionColor={selectionColor}
          hoverColor={hoverColor}
          setCounty={setCounty}
          setCountyCode={setCountyCode}
          county={county}
          countyCode={countyCode}
          toolTipFormatterFunction={toolTipFormatterFunction}
          familyType={familyType}
          settingType={settingType}
          childAge={childAge}
          displayFacilityType={displayFacilityType}
          displayFamilyType={displayFamilyType}
          displayChildAge={displayChildAge}
        />
      </ResponsiveContainer>
    </>
  );
}

export default CountyMap;
